import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { SITE } from '@/constants/testIds';
import { QUOTE_MAILTO } from '@/constants/contact';

const services = [
  {
    id: SITE.serviceCommercial,
    title: 'Commercial',
    subtitle: 'For lobbies, storefronts, and offices that people notice.',
    body:
      "Recurring routes for Front Range storefronts, studios, and office buildings. Trained crews, low-impact tools, and after-hours schedules so your day isn't interrupted.",
    bullets: [
      'Up to 3 stories',
      'Weekly, bi-weekly & monthly',
      'Storefronts & office buildings',
      'After-hours & pre-open crews',
      'Fully Insured',
    ],
    img: '/images/commercial-window-cleaning.jpg',
    align: 'right',
  },
  {
    id: SITE.serviceResidential,
    title: 'Residential',
    subtitle: 'For the home that treats light like a material.',
    body:
      "From Wash Park bungalows to Cherry Creek estates — interior & exterior, hard-water restoration, screen and track detailing. We arrive with drop cloths, not a truck full of hoses.",
    bullets: [
      'Interior + exterior detail',
      'Screen wash & sill wipe',
      'Track & threshold vacuum',
      'Hard-water mineral removal',
    ],
    img: '/images/residential-window-cleaning.jpg',
    align: 'left',
  },
];

export function Services() {
  return (
    <section
      id="services"
      data-testid={SITE.servicesSection}
      className="relative bg-[hsl(var(--stone))] py-32 lg:py-44"
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-14">
        <div className="grid grid-cols-12 gap-6 mb-24 items-end">
          <div className="col-span-12 lg:col-span-4">
            <span className="label text-foreground/92">— Window Care Services</span>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9 }}
            className="col-span-12 lg:col-span-8"
          >
            <h2 className="font-heading font-light uppercase tracking-[-0.02em] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Commercial
              <span className="font-serif italic lowercase font-light">
                {' '}and{' '}
              </span>
              Residential.
            </h2>
          </motion.div>
        </div>

        <div className="space-y-28">
          {services.map((s) => (
            <motion.article
              key={s.id}
              data-testid={s.id}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
              className="grid grid-cols-12 gap-6 items-center"
            >
              <div
                className={`col-span-12 lg:col-span-6 ${
                  s.align === 'right' ? 'lg:order-2' : ''
                }`}
              >
                <div className="relative overflow-hidden aspect-[4/5] lg:aspect-[5/6] group">
                  <motion.img
                    src={s.img}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover will-change-transform"
                    initial={{ scale: 1.06 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, ease: [0.76, 0, 0.24, 1] }}
                    whileHover={{ scale: 1.04 }}
                  />
                  <div className="absolute inset-0 pointer-events-none" />
                </div>
              </div>
              <div
                className={`col-span-12 lg:col-span-5 lg:col-start-${
                  s.align === 'right' ? '1' : '8'
                }`}
              >
                <h3 className="font-heading font-light uppercase text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
                  {s.title}
                </h3>
                <p className="font-serif italic font-light text-xl lg:text-2xl text-foreground/90 mt-4 lowercase">
                  {s.subtitle}
                </p>
                <p className="mt-6 font-light text-base lg:text-lg leading-relaxed text-foreground/92 max-w-xl">
                  {s.body}
                </p>
                <ul className="mt-8 space-y-2.5 max-w-md">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-4">
                      <span className="mt-2 block w-8 h-px bg-foreground/60" />
                      <span className="font-body text-[15px] text-foreground/95">
                        {b}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href={QUOTE_MAILTO}
                  className="group inline-flex items-center gap-3 mt-10 bg-foreground text-background px-6 py-4 label hover:bg-[hsl(var(--gold))] hover:text-foreground transition-colors"
                >
                  Request a Quote <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
